import React, { useState, useEffect } from 'react';
import './DiscountsModal.css';
import { FaTimes, FaPercent, FaDollarSign, FaGift, FaUserPlus, FaUsers } from 'react-icons/fa';

function DiscountsModal({ service, onClose, onSave }) {
  const [discounts, setDiscounts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeTab, setActiveTab] = useState('discounts');
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    // Cargar descuentos y paquetes existentes del servicio
    if (service) {
      setDiscounts(service.discounts || []);
      setPackages(service.packages || []);
    }
  }, [service]);

  const addDiscount = () => {
    const newDiscount = {
      id: Date.now(),
      name: '',
      type: 'percentage', // percentage, fixed, first_time, loyalty
      value: 0,
      conditions: {
        minBookings: 0,
        maxBookings: null,
        validFrom: '',
        validUntil: '',
        daysOfWeek: [],
        timeSlots: []
      },
      active: true
    };
    setDiscounts([...discounts, newDiscount]);
  };

  const addPackage = () => {
    const newPackage = {
      id: Date.now(),
      name: '',
      sessions: 1,
      price: 0,
      validityDays: 30,
      description: '',
      benefits: [],
      active: true
    };
    setPackages([...packages, newPackage]);
  };

  const updateDiscount = (id, field, value) => {
    setDiscounts(discounts.map(discount => 
      discount.id === id ? { ...discount, [field]: value } : discount
    ));
  };

  const updateDiscountCondition = (id, conditionField, value) => {
    setDiscounts(discounts.map(discount => 
      discount.id === id 
        ? { ...discount, conditions: { ...discount.conditions, [conditionField]: value } }
        : discount
    ));
  };

  const updatePackage = (id, field, value) => {
    setPackages(packages.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const removeDiscount = (id) => {
    setDiscounts(discounts.filter(discount => discount.id !== id));
  };

  const removePackage = (id) => {
    setPackages(packages.filter(pkg => pkg.id !== id));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave({
        serviceId: service.id,
        discounts,
        packages
      });
      onClose();
    } catch (error) {
      console.error('Error saving discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 250);
  };

  const getDiscountIcon = (type) => {
    switch(type) {
      case 'percentage': return <FaPercent />;
      case 'fixed': return <FaDollarSign />;
      case 'first_time': return <FaUserPlus />;
      case 'loyalty': return <FaUsers />;
      default: return <FaGift />;
    }
  };

  const dayNames = {
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mié',
    thursday: 'Jue',
    friday: 'Vie',
    saturday: 'Sáb',
    sunday: 'Dom'
  };

  if (!service && !closing) return null;

  return (
    <div className={`discounts-modal-overlay${closing ? ' closing' : ''}`}>
      <div className={`discounts-modal${closing ? ' closing' : ''}`}>
        <div className="modal-header">
          <h2>Descuentos y Promociones - {service?.name}</h2>
          <button className="close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-tabs">
          <button 
            className={`tab ${activeTab === 'discounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('discounts')}
          >
            Descuentos
          </button>
          <button 
            className={`tab ${activeTab === 'packages' ? 'active' : ''}`}
            onClick={() => setActiveTab('packages')}
          >
            Paquetes de Turnos
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'discounts' && (
            <div className="discounts-section">
              <div className="section-header">
                <h3>Configurar Descuentos</h3>
                <button className="add-btn" onClick={addDiscount}>
                  + Agregar Descuento
                </button>
              </div>

              <div className="discounts-list">
                {discounts.map(discount => (
                  <div key={discount.id} className="discount-card">
                    <div className="card-header">
                      <input
                        type="text"
                        placeholder="Nombre del descuento"
                        value={discount.name}
                        onChange={(e) => updateDiscount(discount.id, 'name', e.target.value)}
                        className="discount-name"
                      />
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={discount.active}
                          onChange={(e) => updateDiscount(discount.id, 'active', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="discount-config">
                      <div className="config-row">
                        <label>Tipo de descuento</label>
                        <select 
                          value={discount.type}
                          onChange={(e) => updateDiscount(discount.id, 'type', e.target.value)}
                        >
                          <option value="percentage">Porcentaje</option>
                          <option value="fixed">Monto fijo</option>
                          <option value="first_time">Primera vez</option>
                          <option value="loyalty">Fidelidad</option>
                        </select>
                      </div>

                      <div className="config-row">
                        <label>Valor del descuento</label>
                        <div className="input-group">
                          {discount.type === 'percentage' ? (
                            <>
                              <input
                                type="number"
                                value={discount.value}
                                onChange={(e) => updateDiscount(discount.id, 'value', parseFloat(e.target.value))}
                                min="0"
                                max="100"
                              />
                              <span className="input-addon">%</span>
                            </>
                          ) : (
                            <>
                              <span className="input-addon">$</span>
                              <input
                                type="number"
                                value={discount.value}
                                onChange={(e) => updateDiscount(discount.id, 'value', parseFloat(e.target.value))}
                                min="0"
                              />
                            </>
                          )}
                        </div>
                      </div>

                      {discount.type === 'loyalty' && (
                        <div className="config-row">
                          <label>Cantidad mínima de reservas</label>
                          <input
                            type="number"
                            value={discount.conditions.minBookings}
                            onChange={(e) => updateDiscountCondition(discount.id, 'minBookings', parseInt(e.target.value))}
                            min="1"
                          />
                        </div>
                      )}

                      <div className="config-row dates">
                        <div>
                          <label>Válido desde</label>
                          <input
                            type="date"
                            value={discount.conditions.validFrom}
                            onChange={(e) => updateDiscountCondition(discount.id, 'validFrom', e.target.value)}
                          />
                        </div>
                        <div>
                          <label>Válido hasta</label>
                          <input
                            type="date"
                            value={discount.conditions.validUntil}
                            onChange={(e) => updateDiscountCondition(discount.id, 'validUntil', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="config-row">
                        <label>Días de la semana</label>
                        <div className="days-selector">
                          {Object.entries(dayNames).map(([day, name]) => (
                            <label key={day} className="day-checkbox">
                              <input
                                type="checkbox"
                                checked={discount.conditions.daysOfWeek?.includes(day) || false}
                                onChange={(e) => {
                                  const days = e.target.checked
                                    ? [...(discount.conditions.daysOfWeek || []), day]
                                    : (discount.conditions.daysOfWeek || []).filter(d => d !== day);
                                  updateDiscountCondition(discount.id, 'daysOfWeek', days);
                                }}
                              />
                              <span>{name}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <button 
                        className="remove-btn"
                        onClick={() => removeDiscount(discount.id)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}

                {discounts.length === 0 && (
                  <div className="empty-state">
                    <p>No hay descuentos configurados</p>
                    <button className="add-btn" onClick={addDiscount}>
                      Agregar primer descuento
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'packages' && (
            <div className="packages-section">
              <div className="section-header">
                <h3>Configurar Paquetes de Turnos</h3>
                <button className="add-btn" onClick={addPackage}>
                  + Agregar Paquete
                </button>
              </div>

              <div className="packages-list">
                {packages.map(pkg => (
                  <div key={pkg.id} className="package-card">
                    <div className="card-header">
                      <input
                        type="text"
                        placeholder="Nombre del paquete"
                        value={pkg.name}
                        onChange={(e) => updatePackage(pkg.id, 'name', e.target.value)}
                        className="package-name"
                      />
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={pkg.active}
                          onChange={(e) => updatePackage(pkg.id, 'active', e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="package-config">
                      <div className="config-row">
                        <label>Cantidad de sesiones</label>
                        <input
                          type="number"
                          value={pkg.sessions}
                          onChange={(e) => updatePackage(pkg.id, 'sessions', parseInt(e.target.value))}
                          min="1"
                        />
                      </div>

                      <div className="config-row">
                        <label>Precio del paquete</label>
                        <div className="input-group">
                          <span className="input-addon">$</span>
                          <input
                            type="number"
                            value={pkg.price}
                            onChange={(e) => updatePackage(pkg.id, 'price', parseFloat(e.target.value))}
                            min="0"
                          />
                        </div>
                      </div>

                      <div className="config-row">
                        <label>Validez (días)</label>
                        <input
                          type="number"
                          value={pkg.validityDays}
                          onChange={(e) => updatePackage(pkg.id, 'validityDays', parseInt(e.target.value))}
                          min="1"
                        />
                      </div>

                      <div className="config-row">
                        <label>Descripción</label>
                        <textarea
                          value={pkg.description}
                          onChange={(e) => updatePackage(pkg.id, 'description', e.target.value)}
                          placeholder="Describe los beneficios del paquete"
                          rows="3"
                        />
                      </div>

                      <div className="savings-info">
                        {service && pkg.sessions > 0 && pkg.price > 0 && (
                          <p>
                            Ahorro: <strong>${(service.price * pkg.sessions - pkg.price).toFixed(0)}</strong> 
                            ({((1 - pkg.price / (service.price * pkg.sessions)) * 100).toFixed(0)}% de descuento)
                          </p>
                        )}
                      </div>

                      <button 
                        className="remove-btn"
                        onClick={() => removePackage(pkg.id)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}

                {packages.length === 0 && (
                  <div className="empty-state">
                    <p>No hay paquetes configurados</p>
                    <button className="add-btn" onClick={addPackage}>
                      Agregar primer paquete
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="save-btn" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DiscountsModal; 